import { Log } from './log';
import 'knockout.validation';
import ko from 'knockout';
import MicroModal from 'micromodal';
import { differenceInMinutes } from 'date-fns';
import { decodeHTML } from './decode-html';
import {
    type PexipParticipant,
    type PexRTC,
    SpeakerLayout,
} from './interfaces-pexip';
import { Heartbeat } from './heartbeat';
import { MicrophoneAnalyzer } from './mic-analyzer';
import Cookies from 'js-cookie';
import { VideoUtils, Browser } from './video-utils';
import { ThrottledLogger } from './throttled-logger';
import { ParticipantListViewModel } from './participant-list-view-model';
import {
    Message,
    CommandMessage,
    MicrophoneStatusMessage,
    CommandType,
} from './message';
import { DropdownItemModel } from './dropdown-item-model';

declare const PexRTC: PexRTC;

type ParticipantWithMetadata = {
    readonly uuid: string;
    readonly info: PexipParticipant;
    readonly hasActiveVoice?: boolean;
};

enum DeviceStatus {
    // Access have not been requested yet
    notRequested,
    // Device is requested, but user have not given response yet. This might never change
    waitingForUser,
    // There are no matching devices on system
    notFound,
    // User decided not to grant access, either through browser or OS
    denied,
    // User granted access, but disabled afterwards (muted)
    disabledByUser,
    // Access is granted
    granted,
}

class VDXCallViewModel {
    private validator: KnockoutValidationGroup;
    private localVideoElement: HTMLVideoElement | undefined;
    private remoteVideoElement: HTMLVideoElement | undefined;
    private screenshareVideoElement: HTMLVideoElement | undefined;
    private volumeBar:
        | {
              container: HTMLVideoElement;
              bar: HTMLDivElement;
              barDirection: 'vertical' | 'horizontal';
          }
        | undefined;
    public isMicAnalyzable = ko.observable(false);
    public micVolume = ko.observable(0);
    private isZeroVolumeRegisteredByAnalyser = ko.observable(false);
    // This is not computed because there is a timer for how long it has been zero, and this logic is handled by MicrophoneAnalyser class
    public isZeroVolume = ko.pureComputed(
        () =>
            this.isZeroVolumeRegisteredByAnalyser() &&
            !this.microphonePrivacy(),
    );
    public hasRegisteredActiveLocalSpeech = ko.observable(false);
    private selectedLayoutOption = ko.observable<SpeakerLayout>(
        SpeakerLayout._4_0,
    );

    private connectToRemote: boolean;
    private micAnalyzer: MicrophoneAnalyzer | null;
    private hasShownModal: boolean;
    private lastShownModalID: string | null | undefined;

    private microphones = ko.observableArray<MediaDeviceInfo>([]);
    private speakers = ko.observableArray<MediaDeviceInfo>([]);
    private cameras = ko.observableArray<MediaDeviceInfo>([]);

    public cameraDropdownItems = ko.observableArray<DropdownItemModel>([]);
    public microphoneDropdownItems = ko.observableArray<DropdownItemModel>([]);
    public speakerDropdownItems = ko.observableArray<DropdownItemModel>([]);
    public isLocalVideoUserFacing = ko.observable(false);

    private selectedMicrophone = ko.observable<MediaDeviceInfo>(null);
    private selectedSpeaker = ko.observable<MediaDeviceInfo>(null);
    private selectedCamera = ko.observable<MediaDeviceInfo>(null);

    public browser = ko.observable<string>(null);
    public hasParticipantStatus = ko.pureComputed(
        () => this.participantStatus() != '',
    );
    public isParticipantListVisible = ko.observable<boolean>(false);
    public canSelectSpeaker = ko.observable<boolean>(
        VideoUtils.isMediaElementSinkSupported(),
    );

    private remoteVoiceActivityActiveLogger = new ThrottledLogger((key) => {
        this.remoteVoiceActivityInactiveLogger.flush(key);
        Log.info(`Voice Activity detected for remote participant ${key}`);
    });

    private remoteVoiceActivityInactiveLogger = new ThrottledLogger((key) => {
        Log.info(`Voice Activity ceased for remote participant ${key}`);
    });

    private zeroVolumeLog = new ThrottledLogger((isZeroVolume) => {
        if (isZeroVolume) {
            this.zeroVolumeLog.flush(false);
            Log.info('Zero volume was registered for 1s');
        } else {
            Log.info('Volume was registered after being zero');
        }
    });

    private localStream: MediaStream | null = null;

    private disableMicrophoneOnConnectTreshold = 4;
    private defaultCameraIndex = 0;
    private defaultMicrophoneIndex = 0;
    private defaultSpeakerIndex = 0;

    constructor(
        node: string | null,
        conference: string | null,
        displayName: string | null,
        pin: number | null,
        meetingID: number | null,
        videoSessionID: number | null,
        startTime: string | null,
        remoteVideoElementId: string | null,
        localVideoElementId: string | null,
        screenshareVideoElementId: string | null,
    ) {
        if (meetingID) Log.addStaticAdditionalData('meetingId', meetingID);

        this.browser(Browser[VideoUtils.getBrowser()]);
        this.connectToRemote =
            node != null && conference != null && displayName != null;
        this.pexRTC = new PexRTC();
        this.videoSessionID = videoSessionID;
        if (startTime != null) {
            this.startTime = new Date(startTime);
        }

        this.initPreflightForm(displayName);

        if (screenshareVideoElementId) {
            this.screenshareVideoElement = document.getElementById(
                screenshareVideoElementId,
            ) as HTMLVideoElement;
        }
        if (localVideoElementId) {
            this.localVideoElement = document.getElementById(
                localVideoElementId,
            ) as HTMLVideoElement;
        }
        if (remoteVideoElementId != null) {
            this.remoteVideoElement = document.getElementById(
                remoteVideoElementId,
            ) as HTMLVideoElement;
        }

        this.setupMeetingStartedTimer();

        this.cameraStatus.subscribe((status) => {
            switch (status) {
                case DeviceStatus.granted:
                    this.setPreferredCameraDisabled(false);
                    break;
                case DeviceStatus.disabledByUser:
                    this.setPreferredCameraDisabled(true);
                    break;
            }
        });
        this.microphoneStatus.subscribe((status) => {
            switch (status) {
                case DeviceStatus.granted:
                    this.setPreferredMicrophoneDisabled(false);
                    break;
                case DeviceStatus.disabledByUser:
                    this.setPreferredMicrophoneDisabled(true);
                    break;
            }
        });

        this.pexRTC.onLog = (...args) => {
            // We handle this case differently to ensure better logging for this error
            if (args[0] === 'getUserMedia error') {
                const error = args[1];
                const message: {
                    message: string;
                    error: unknown;
                    errorType: string;
                    constraint: null | string;
                } = {
                    message: 'getUserMedia error',
                    error,
                    errorType: Object.prototype.toString.call(error),
                    constraint: null,
                };

                if (
                    window.OverconstrainedError &&
                    error instanceof OverconstrainedError
                ) {
                    message.errorType = error.name ?? message.errorType;
                    message.constraint = error.constraint;
                }

                Log.info('PexipLog', message);
            } else {
                Log.info('PexipLog', { arguments: args });
            }
        };

        this.pexRTC.onSetup = (stream) => {
            if (pin == null) return;
            this.pexRTC.connect(pin);
            if (stream instanceof MediaStream) {
                this.updateLocalStream(stream);
            }
        };
        this.pexRTC.onConnect = (stream) => {
            this.uuid = this.pexRTC.uuid;
            this.role(this.pexRTC.role ?? '');

            if (this.uuid != null) {
                Log.info(`we are ${this.uuid}. Role is ${this.role()}`);
            } else {
                Log.info(`we are not logged in. Role is ${this.role()}`);
            }

            if (this.role() === 'HOST') {
                this.pexRTC.transformLayout({
                    layout: this.selectedLayoutOption(),
                    enable_active_speaker_indication: true,
                    enable_overlay_text: true,
                });
                this.selectedLayoutOption.subscribe((layout) => {
                    Log.info('updating layout:', { layout });
                    this.pexRTC.transformLayout({
                        layout,
                    });
                });
            }

            this.isLoadingVisible(false);
            this.isReady(true);

            if (this.remoteVideoElement != null) {
                if (stream instanceof MediaStream) {
                    this.remoteVideoElement.srcObject = stream;
                }
            }

            const allParticipants = this.participants.peek();
            const participantIdsExcludingSelf = Object.keys(
                allParticipants,
            ).filter((x) => x !== this.uuid);

            // Participants already present when we join are reported before this point.
            // We make the participantsChange call to register that fact.
            if (participantIdsExcludingSelf.length > 0) {
                this.participantsChanged(participantIdsExcludingSelf);
            }
            this.participants.subscribe((allParticipants) => {
                const activeSpeakers = Object.values(allParticipants).filter(
                    (x) => x.hasActiveVoice,
                );
                if (activeSpeakers.length === 0) {
                    this.participantStatus('');
                } else {
                    const firstSpeaker =
                        activeSpeakers.find((x) => x.uuid !== this.uuid) ??
                        activeSpeakers[0];
                    const name = firstSpeaker.info.display_name;
                    this.participantStatus(name + ' taler');
                }
            });

            this.updateParticipantListWithCurrentMuteStatus();

            // Inform other call participants of current microphone mute status
            this.broadCastMicrophoneStatus();

            // Disable microphone of new participants if the number of participants excluding self
            // surpasses disableMicrophoneOnConnectTreshold
            if (
                participantIdsExcludingSelf.length >
                this.disableMicrophoneOnConnectTreshold
            ) {
                this.microphoneStatus(DeviceStatus.disabledByUser);
                this.setMutedForTracks(this.microphonePrivacy(), 'audio');
                this.microphoneDisabledOnConnect(true);

                // Reset microphoneDisabledOnConnect
                setTimeout(() => {
                    this.microphoneDisabledOnConnect(false);
                }, 10_000);
            }

            // We only want to animate the changes from 0 to >0.
            this.shouldAnimateLocalView(
                participantIdsExcludingSelf.length === 0,
            );

            this.connectToMeeting();
        };
        this.pexRTC.onError = (err) => {
            Log.error(err);
            this.showModal('unknown-video-error-modal');
            const reasonElement = document.querySelector(
                '#unknown-video-error-modal__reason',
            );
            if (reasonElement != null) {
                reasonElement.innerHTML = err;
            }
        };
        this.pexRTC.onDisconnect = (reason) => {
            Log.warn(`Pexip disconnected us: ${reason}`);
            this.disconnect();
        };
        this.pexRTC.onParticipantCreate = (participant) => {
            const currentParticipants = this.participants();
            currentParticipants[participant.uuid] = {
                uuid: participant.uuid,
                info: participant,
            };
            this.participants(currentParticipants);

            const participantModel = new ParticipantListViewModel(
                participant.uuid,
                participant.display_name,
                participant.role === 'chair',
                participant.buzz_time > 0,
                (uuids) => this.toggleMicrophoneForParticipants(uuids),
                (uuid) => this.lowerHandForParticipant(uuid),
            );
            let currentList = this.participantList();
            currentList.push(participantModel);
            currentList = currentList.sort((a, b) =>
                a.displayName.localeCompare(b.displayName),
            );
            this.participantList(currentList);

            // The system tells us that we got added. We ignore that.
            if (participant.uuid == this.uuid) {
                Log.info('we got added, ignoring');
            } else {
                Log.info(`got new participant: ${participant.uuid}`, {
                    participant,
                });

                // Inform new user of current microphone status
                this.broadCastMicrophoneStatus();
            }
            const participantIdsExcludingSelf = Object.keys(
                currentParticipants,
            ).filter((x) => x !== this.uuid);
            this.participantsChanged(participantIdsExcludingSelf);
        };
        this.pexRTC.onParticipantDelete = (participant) => {
            const currentParticipants = this.participants();
            delete currentParticipants[participant.uuid];
            this.participants(currentParticipants);

            let currentList = this.participantList();
            currentList = currentList.filter((p) => p.uuid != participant.uuid);
            this.participantList(currentList);

            this.numberOfHandsRaised(
                this.participantList().filter((p) => p.isHandRaised()).length,
            );

            Log.info(`removed participant: ${participant.uuid}`, {
                participant,
            });
            const participantIdsExcludingSelf = Object.keys(
                currentParticipants,
            ).filter((x) => x !== this.uuid);
            this.participantsChanged(participantIdsExcludingSelf);
        };
        this.pexRTC.onParticipantUpdate = (participant: PexipParticipant) => {
            this.participants()[participant.uuid] = {
                uuid: participant.uuid,
                info: participant,
            };

            for (const p of this.participantList()) {
                if (p.uuid === participant.uuid) {
                    p.isHandRaised(participant.buzz_time > 0);
                }
            }

            this.numberOfHandsRaised(
                this.participantList().filter((p) => p.isHandRaised()).length,
            );

            if (participant.uuid === this.uuid) {
                this.isHandRaised(participant.buzz_time > 0);
            }
        };
        // Presentation is one remote sharing with local
        this.pexRTC.onPresentation = (isPresenting) => {
            if (isPresenting) {
                this.pexRTC.getPresentation();
            } else {
                this.isReceivingScreenshare(false);
                if (this.screenshareVideoElement) {
                    this.screenshareVideoElement.srcObject = null;
                }
            }
        };
        this.pexRTC.onPresentationConnected = (stream) => {
            if (this.screenshareVideoElement && stream instanceof MediaStream) {
                this.isReceivingScreenshare(true);
                this.screenshareVideoElement.srcObject = stream;
            }
        };
        this.pexRTC.onPresentationStopped =
            this.pexRTC.onPresentationDisconnected = (reason) => {
                Log.info('presentation stopped', { reason });
            };
        // Screenshare is local browser sharing with remotes
        this.pexRTC.onScreenshareConnected = (stream) => {
            this.isScreenShared(true);
            if (this.screenshareVideoElement && stream instanceof MediaStream) {
                this.screenshareVideoElement.srcObject = stream;
            }
        };
        this.pexRTC.onScreenshareStopped = (reason) => {
            Log.info('screenshare stopped', { reason });
            this.isScreenShared(false);
        };
        this.pexRTC.onStageUpdate = (stages) => {
            const currentParticipants = this.participants();
            let dataWasUpdated = false;
            for (const stage of stages) {
                let data = currentParticipants[stage.participant_uuid];
                if (data == null) {
                    Log.warn(
                        `Received stage update for unknown participant: ${stage.participant_uuid}`,
                    );
                    continue;
                }

                if (stage.vad === 100 && !data.hasActiveVoice) {
                    this.remoteVoiceActivityActiveLogger.log(data.uuid);

                    data = {
                        ...data,
                        hasActiveVoice: true,
                    };
                } else if (stage.vad === 0 && data.hasActiveVoice) {
                    this.remoteVoiceActivityInactiveLogger.log(data.uuid);

                    data = {
                        ...data,
                        hasActiveVoice: false,
                    };
                }

                if (data !== currentParticipants[stage.participant_uuid]) {
                    dataWasUpdated = true;
                    currentParticipants[stage.participant_uuid] = data;
                }
            }
            if (dataWasUpdated) {
                this.participants(currentParticipants);
            }
        };

        // False means no video. We will set this later
        this.pexRTC.video_source = false;
        this.pexRTC.audio_source = false;

        // 2000ms delay for showing the modal has been set as an arbitrary value through testing.
        // Hardware and browsers behave differently, and thus have different execution time of
        // getUserMedia even if access is granted
        const infoTimer = setTimeout(() => {
            this.showModal('requestdeviceaccessmodal');
        }, 2_000);

        this.requestDeviceAccess()
            .then(async (stream) => {
                clearTimeout(infoTimer);
                this.closeModal('requestdeviceaccessmodal');
                await this.setupInputOutputSelects();

                this.logCurrentDeviceStatus('Devices initialized');

                if (this.connectToRemote) {
                    this.pexRTC.makeCall(
                        node,
                        conference,
                        displayName,
                        null,
                        null,
                        null,
                    );
                    // Stop tracks of unused stream
                    stream.getTracks().forEach((track) => track.stop());
                } else {
                    if (stream != null) {
                        this.updateLocalStream(stream);
                    }
                    if (this.localVideoElement != null) {
                        this.setMutedForTracks(this.cameraPrivacy(), 'video');
                        this.setMutedForTracks(
                            this.microphonePrivacy(),
                            'audio',
                        );

                        this.isLoadingVisible(false);
                        this.isReady(true);
                    }
                }
            })
            .catch((e) => {
                Log.error(e);
            });
    }

    private lowerHandForParticipant(uuid: string) {
        this.pexRTC.clearBuzz(uuid);
    }

    public muteAll() {
        const participantsExludingSelf = this.participantList().filter(
            (p: ParticipantListViewModel) => p.uuid != this.uuid,
        );

        participantsExludingSelf.forEach((p: ParticipantListViewModel) => {
            p.isMicrophoneMuted(true);
            p.dirtyMuteStateCounter++;
        });

        const uuids = participantsExludingSelf.map(
            (p: ParticipantListViewModel) => p.uuid,
        );

        const message = new CommandMessage(CommandType.MuteMicrophone, uuids);
        this.sendChat(message);
    }

    public toggleRaiseHand() {
        this.isHandRaised(!this.isHandRaised());

        const self = this.participants()[this.uuid];
        if (self.info.buzz_time > 0) {
            this.pexRTC.clearBuzz(this.uuid);
        } else {
            this.pexRTC.setBuzz();
        }
    }

    private toggleMicrophoneForParticipants(uuids: string[]) {
        if (uuids.includes(this.uuid)) {
            this.toggleMicrophone();
            uuids = uuids.filter((uuid) => uuid != this.uuid);
            if (uuids.length == 0) return;
        }

        const message = new CommandMessage(CommandType.ToggleMicrophone, uuids);
        this.sendChat(message);
    }

    private broadCastMicrophoneStatus() {
        const message = new MicrophoneStatusMessage(this.microphonePrivacy());
        this.sendChat(message);
    }

    private sendChat(message: Message) {
        if (!this.connectToRemote) {
            return;
        }

        // We don't support participant-list yet, so there is no need to actually send the message
        //this.pexRTC.sendChatMessage(JSON.stringify(message));

        // This is to make the lint happy about message being used
        message?.toString?.();
    }

    public hideParticipantList() {
        this.isParticipantListVisible(false);
    }

    public showParticipantList() {
        this.isParticipantListVisible(true);
    }

    private updateParticipantListWithCurrentMuteStatus() {
        for (const participant of this.participantList()) {
            if (participant.uuid === this.uuid) {
                participant.isMicrophoneMuted(this.microphonePrivacy());
            }
        }
    }

    public showModal(modalID: string) {
        Log.info('Showing modal', {
            modalID,
            closingExisting: this.hasShownModal,
        });
        // Due to buggy behavior from MicroModal we have to handle MicroModal life-cycle manually
        if (this.hasShownModal) {
            MicroModal.close();
        }
        this.hasShownModal = true;
        this.lastShownModalID = modalID;
        MicroModal.show(modalID, {
            awaitCloseAnimation: true,
            disableFocus: true,
            onClose: () => {
                this.lastShownModalID = null;
                this.hasShownModal = false;
            },
        });
    }

    public closeModal(modalID: string) {
        Log.info('asked to close modal', {
            modalID,
            lastShownModalID: this.lastShownModalID,
        });
        // Due to buggy behavior from MicroModal we check to see if we have opened at least one MicroModal.
        // Trying to close a modal that have not been shown will throw exception from MicroModal,
        // unless any Modal have been opened at least once, then close function will work without exception
        // even if attempting to close a model that have not been shown or is non-existing.
        if (this.lastShownModalID === modalID) {
            MicroModal.close(modalID);
            this.lastShownModalID = null;
        }
    }

    private setupMicAnalyzer(stream: MediaStream) {
        if (stream.getAudioTracks().length === 0) {
            this.isMicAnalyzable(false);
            this.micAnalyzer?.close();
            this.micAnalyzer = null;
            return;
        }

        if (this.micAnalyzer != null) {
            this.micAnalyzer.setStream(stream);
            return;
        }

        this.micAnalyzer = new MicrophoneAnalyzer(stream);
        this.isMicAnalyzable(true);
        this.micAnalyzer.onZeroVolumeChanged = (isZeroVolume) => {
            this.zeroVolumeLog.log(isZeroVolume);
            this.isZeroVolumeRegisteredByAnalyser(isZeroVolume);
        };

        this.micAnalyzer.onVolumeActivityChanged = () => {
            if (!this.hasRegisteredActiveLocalSpeech()) {
                this.hasRegisteredActiveLocalSpeech(true);
                Log.info('First audio from local mic');
            }
        };
        this.updateMicVolume();
    }

    private updateMicVolume() {
        if (this.micAnalyzer == null) {
            return;
        }

        const vol = this.microphonePrivacy() ? 0 : this.micAnalyzer.getVol();
        this.micVolume(vol);

        requestAnimationFrame(() => this.updateMicVolume());
    }

    private updateLocalStream(stream: MediaStream) {
        this.localStream?.getTracks().forEach((track) => track.stop());
        this.localStream = stream;

        if (this.localVideoElement != null) {
            this.localVideoElement.srcObject = stream;
            const facingMode = stream
                .getVideoTracks()[0]
                ?.getSettings().facingMode;
            // chrome on mac does not expose facingMode, default to mirror
            const isUserFacing =
                facingMode === 'user' || facingMode === undefined;
            this.isLocalVideoUserFacing(isUserFacing);
        }

        // Restore privacy state on new stream
        this.setMutedForTracks(this.cameraPrivacy(), 'video');
        this.setMutedForTracks(this.microphonePrivacy(), 'audio');

        this.setupMicAnalyzer(stream);
    }

    private async requestDeviceAccess(): Promise<MediaStream | null> {
        function mapGUMError(e: Error): DeviceStatus {
            // Safari exposes the OverconstrainedError as a separate error
            if (
                window.OverconstrainedError &&
                e instanceof window.OverconstrainedError
            ) {
                return DeviceStatus.notFound;
            }
            if (e instanceof DOMException) {
                switch (e.name) {
                    case 'NotFoundError':
                        return DeviceStatus.notFound;
                    case 'NotAllowedError':
                        return DeviceStatus.denied;
                }
            }

            return DeviceStatus.denied;
        }

        const preferredDeviceStatus = this.getPreferredDeviceStatus();
        const cameraGrantedStatus = preferredDeviceStatus.isCameraDisabled
            ? DeviceStatus.disabledByUser
            : DeviceStatus.granted;
        const microphoneGrantedStatus =
            preferredDeviceStatus.isMicrophoneDisabled
                ? DeviceStatus.disabledByUser
                : DeviceStatus.granted;

        const hadCameraAlready =
            this.cameraStatus() === DeviceStatus.granted ||
            this.cameraStatus() === DeviceStatus.disabledByUser;
        if (!hadCameraAlready) {
            this.cameraStatus(DeviceStatus.waitingForUser);
        }
        const hadMicrophoneAlready =
            this.microphoneStatus() === DeviceStatus.granted ||
            this.microphoneStatus() === DeviceStatus.disabledByUser;
        if (!hadMicrophoneAlready) {
            this.microphoneStatus(DeviceStatus.waitingForUser);
            this.speakerStatus(DeviceStatus.waitingForUser);
        }

        const audioConstraints = {
            deviceId: preferredDeviceStatus.microphoneID,
        };
        const videoConstraints = {
            facingMode: 'user', // or "environment" for rear
            deviceId: preferredDeviceStatus.cameraID,
        };

        // Reset existing stream before requesting a new
        this.localStream?.getTracks().forEach((track) => track.stop());
        this.localStream = null;

        let stream: MediaStream | null = null;
        // We try both first, since that gives the user a combine permissions-dialog
        try {
            stream = await navigator.mediaDevices.getUserMedia({
                audio: audioConstraints,
                video: videoConstraints,
            });
            this.microphoneStatus(microphoneGrantedStatus);
            this.cameraStatus(cameraGrantedStatus);
        } catch {
            // We then try individually, since the user might only have camera or mic
            // Asking multiple times does not hurt. The browsers remember that the user said yes, and returns that answer immediately
            try {
                stream = await navigator.mediaDevices.getUserMedia({
                    audio: audioConstraints,
                });
                this.microphoneStatus(microphoneGrantedStatus);
            } catch (e) {
                this.microphoneStatus(mapGUMError(e));
            }

            try {
                stream = await navigator.mediaDevices.getUserMedia({
                    video: videoConstraints,
                });
                this.cameraStatus(cameraGrantedStatus);
            } catch (e) {
                this.cameraStatus(mapGUMError(e));
            }

            // We ask for the combined, because it changes how the re-ask-permissions work in browsers
            // We don't care about the promise. We already handled the response above. This is only
            // for priming that dialog. Catching for silencing uncaught exception
            navigator.mediaDevices
                .getUserMedia({
                    audio: audioConstraints,
                    video: videoConstraints,
                })
                .catch(() => {
                    // Silencing uncaught exception
                });
        }

        if (
            this.cameraStatus() === DeviceStatus.denied ||
            this.microphoneStatus() === DeviceStatus.denied
        ) {
            Log.info('Device access blocked');
            this.showModal('devices-blocked-modal');
        }

        if (
            this.microphoneStatus() === DeviceStatus.granted &&
            this.pexRTC.audio_source != null
        ) {
            // Null means use default source
            this.pexRTC.audio_source = null;
            // This makes PexRTC ask for permissions. We only do this if we have connected to Pexip
            if (this.uuid) {
                this.pexRTC.addCall(null);
            }
        }

        const isFirefox = VideoUtils.getBrowser() === Browser.Firefox;

        //setting video source if we have any camera and granted access
        if (this.cameraStatus() === cameraGrantedStatus && !hadCameraAlready) {
            this.pexRTC.muteVideo(this.cameraPrivacy());

            const hasCameraId = preferredDeviceStatus.cameraID != null;
            if (hasCameraId) {
                if (isFirefox) {
                    this.pexRTC.video_source = {
                        exact: preferredDeviceStatus.cameraID,
                    };
                } else if (hasCameraId) {
                    this.pexRTC.video_source = preferredDeviceStatus.cameraID;
                }
            } else {
                this.pexRTC.video_source = null;
            }
        }

        //setting audio source if we have any camera and granted access
        if (
            this.microphoneStatus() === microphoneGrantedStatus &&
            !hadMicrophoneAlready
        ) {
            this.pexRTC.muteAudio(this.microphonePrivacy());

            const hasMicrophoneId = preferredDeviceStatus.microphoneID != null;
            if (hasMicrophoneId) {
                if (isFirefox) {
                    this.pexRTC.audio_source = {
                        exact: preferredDeviceStatus.microphoneID,
                    };
                } else {
                    this.pexRTC.audio_source =
                        preferredDeviceStatus.microphoneID;
                }
            } else {
                this.pexRTC.audio_source = null;
            }
        }

        navigator.mediaDevices.ondevicechange = this.updateDevices;
        await this.updateDevices();

        return stream;
    }

    private updateDevices = async () => {
        if (navigator.mediaDevices?.enumerateDevices == null) {
            this.speakerStatus(DeviceStatus.notFound);
            return;
        }
        const devices = await navigator.mediaDevices.enumerateDevices();
        const defaultDevices = devices.filter((d) => d.deviceId === 'default');
        const devicesWithoutDefault = devices.filter(
            (d) => d.deviceId !== 'default',
        );

        const videoinputs = devicesWithoutDefault.filter(
            (d) => d.kind === 'videoinput',
        );
        const audioinputs = devicesWithoutDefault.filter(
            (d) => d.kind === 'audioinput',
        );
        const audiooutputs = devicesWithoutDefault.filter(
            (d) => d.kind === 'audiooutput',
        );
        Log.info('VideoInputs', videoinputs);
        Log.info('AudioInputs', audioinputs);
        Log.info('AudioOutputs', audiooutputs);

        defaultDevices.forEach((device) => {
            switch (device.kind) {
                case 'videoinput': {
                    const index = videoinputs.findIndex(
                        (d) => d.groupId == device.groupId,
                    );
                    if (index > -1) {
                        this.defaultCameraIndex = index;
                    }
                    break;
                }
                case 'audioinput': {
                    const index = audioinputs.findIndex(
                        (d) => d.groupId == device.groupId,
                    );
                    if (index > -1) {
                        this.defaultMicrophoneIndex = index;
                    }
                    break;
                }
                case 'audiooutput': {
                    const index = audiooutputs.findIndex(
                        (d) => d.groupId == device.groupId,
                    );
                    if (index > -1) {
                        this.defaultSpeakerIndex = index;
                    }
                    break;
                }
                default:
                    Log.error(
                        'Unknown input/output device of kind; ' + device.kind,
                    );
            }
        });

        // Support for speakers through the mediaDevices API is not complete.
        // They cannot be requested directly, and only Chrome currently supports them.
        // Also, their status is tied strongly to audio, in that if the user says no to grant mic access,
        // they also denies support for changing speaker. So we translate the micStatus into speaker-status here.
        switch (this.microphoneStatus()) {
            case DeviceStatus.notRequested:
            case DeviceStatus.waitingForUser:
            case DeviceStatus.denied:
                this.speakerStatus(this.microphoneStatus());
                break;
            case DeviceStatus.notFound:
            case DeviceStatus.disabledByUser:
            case DeviceStatus.granted:
                if (audiooutputs.length > 0) {
                    this.speakerStatus(DeviceStatus.granted);
                } else {
                    this.speakerStatus(DeviceStatus.notFound);
                }
                break;
        }

        this.cameras(videoinputs);
        this.microphones(audioinputs);
        this.speakers(audiooutputs);
    };

    private connectToMeeting(): void {
        if (this.videoSessionID == null) return;
        Heartbeat.start(this.videoSessionID);

        this.registerUnloadHandler();
    }

    private registerUnloadHandler(): void {
        window.addEventListener('beforeunload', () => {
            if (this.disconnecting()) return;
            this.disconnecting(true);

            const url = '/api/meetings/leave/' + this.videoSessionID;
            if (navigator.sendBeacon) {
                navigator.sendBeacon(url);
            } else {
                const req = new XMLHttpRequest();
                req.open('POST', url, false);
                req.send();
            }

            this.pexRTC.disconnect();
        });
    }

    private setupMeetingStartedTimer() {
        const onTimer = () => {
            const startedMinutesAgo = differenceInMinutes(
                new Date(),
                this.startTime,
            );
            this.meetingStartedMinutesAgo(startedMinutesAgo);

            const joinedMinutesAgo = differenceInMinutes(
                new Date(),
                this.joinedOn,
            );
            const maxWaitingTime = 70;

            if (
                joinedMinutesAgo > maxWaitingTime &&
                startedMinutesAgo > maxWaitingTime &&
                !this.firstParticipantJoined()
            ) {
                this.disconnect();
            }
        };
        onTimer();
        setInterval(onTimer, 10_000);
    }

    private async setupInputOutputSelects() {
        this.selectedSpeaker.subscribe((device) => {
            this.logDevice('Speaker', device.deviceId);
            this.setPreferredSpeaker(device.deviceId);

            if (device.deviceId) {
                if (!this.connectToRemote) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const localVideo = this.localVideoElement as any;
                    localVideo.setSinkId(device.deviceId).then(() => {
                        document.dispatchEvent(
                            new Event('SpeakerUpdatedEvent'),
                        );
                    });
                } else {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (this.remoteVideoElement as any).setSinkId(device.deviceId);
                }
            }
        });

        this.selectedMicrophone.subscribe((device) => {
            this.logDevice('Microphone', device.deviceId);
            this.setPreferredMicrophone(device.deviceId);

            if (!this.connectToRemote) {
                this.requestDeviceAccess()
                    .then((stream) => {
                        if (stream != null) {
                            this.updateLocalStream(stream);
                        }
                    })
                    .catch((e) => {
                        Log.error(e);
                    });
                return;
            }

            if (this.pexRTC.audio_source !== device.deviceId) {
                const hasDeviceId = device.deviceId != null;

                if (hasDeviceId) {
                    if (VideoUtils.getBrowser() === Browser.Firefox) {
                        this.pexRTC.audio_source = {
                            exact: device.deviceId,
                        };
                    } else {
                        this.pexRTC.audio_source = device.deviceId;
                    }
                } else {
                    // No audio means we don't want to send audio
                    this.pexRTC.audio_source = false;
                }

                // We escape here because we have not connected to pexip yet
                if (!this.uuid) return;

                this.pexRTC.renegotiate();
            }
        });

        this.selectedCamera.subscribe((device) => {
            this.logDevice('Camera', device.deviceId);
            this.setPreferredCamera(device.deviceId);

            if (!this.connectToRemote) {
                this.requestDeviceAccess()
                    .then((stream) => {
                        if (stream != null) {
                            this.updateLocalStream(stream);
                        }
                    })
                    .catch((e) => {
                        Log.error(e);
                    });
                return;
            }

            if (this.pexRTC.video_source !== device.deviceId) {
                const hasDeviceId = device.deviceId != null;

                if (hasDeviceId) {
                    if (VideoUtils.getBrowser() === Browser.Firefox) {
                        this.pexRTC.video_source = { exact: device.deviceId };
                    } else {
                        this.pexRTC.video_source = device.deviceId ?? false;
                    }
                } else {
                    // No cam means we don't want to send video
                    this.pexRTC.video_source = false;
                }

                // We escape here because we have not connected to pexip yet
                if (!this.uuid) return;

                this.pexRTC.renegotiate();
            }
        });

        this.cameras.subscribe((cams) => {
            const currentlySelected = this.selectedCamera();

            const canUpdateSelectedCamera = cams.length > 0;
            const shouldUpdateSelectedCamera =
                currentlySelected == null ||
                !cams.some((x) => x.deviceId === currentlySelected.deviceId);

            if (canUpdateSelectedCamera && shouldUpdateSelectedCamera) {
                this.selectedCamera(cams[this.defaultCameraIndex]);
            }

            const cameraItems: DropdownItemModel[] = [];
            cams.forEach((cam) =>
                cameraItems.push(
                    new DropdownItemModel(cam.label, () => {
                        this.selectedCamera(cam);
                        this.showCameraSelectionDropdown(false);
                    }),
                ),
            );
            cameraItems.sort((a, b) => {
                return a.label.localeCompare(b.label);
            });
            this.cameraDropdownItems(cameraItems);
        });

        this.microphones.subscribe((inputs) => {
            const currentlySelected = this.selectedMicrophone();

            const canUpdateSelectedMicrophone = inputs.length > 0;
            const shouldUpdateSelectedMicrophone =
                currentlySelected == null ||
                !inputs.some((x) => x.deviceId === currentlySelected.deviceId);

            if (canUpdateSelectedMicrophone && shouldUpdateSelectedMicrophone) {
                this.selectedMicrophone(inputs[this.defaultMicrophoneIndex]);
            }

            const microphoneItems: DropdownItemModel[] = [];
            inputs.forEach((input) =>
                microphoneItems.push(
                    new DropdownItemModel(input.label, () => {
                        this.selectedMicrophone(input);
                        this.showMicrophoneSelectionDropdown(false);
                    }),
                ),
            );
            microphoneItems.sort((a, b) => {
                return a.label.localeCompare(b.label);
            });
            this.microphoneDropdownItems(microphoneItems);
        });

        this.speakers.subscribe((outputs) => {
            const currentlySelected = this.selectedSpeaker();

            const canUpdateSelectedSpeaker = outputs.length > 0;
            const shouldUpdateSelectedSpeaker =
                currentlySelected == null ||
                !outputs.some((x) => x.deviceId === currentlySelected.deviceId);

            if (canUpdateSelectedSpeaker && shouldUpdateSelectedSpeaker) {
                this.selectedSpeaker(outputs[this.defaultSpeakerIndex]);
            }

            const speakerItems: DropdownItemModel[] = [];
            outputs.forEach((output) =>
                speakerItems.push(
                    new DropdownItemModel(output.label, () => {
                        this.selectedSpeaker(output);
                        this.showSpeakerSelectionDropdown(false);
                    }),
                ),
            );
            speakerItems.sort((a, b) => {
                return a.label.localeCompare(b.label);
            });
            this.speakerDropdownItems(speakerItems);
        });

        if (
            this.cameras().length > 0 &&
            this.selectedCamera() === null &&
            this.cameraStatus() !== DeviceStatus.denied
        ) {
            const preferredCam = this.cameras().find(
                (x) => x.deviceId === this.getPreferredDeviceStatus().cameraID,
            );
            this.selectedCamera(
                preferredCam ?? this.cameras()[this.defaultCameraIndex],
            );
        }

        if (
            this.microphones().length > 0 &&
            this.selectedMicrophone() === null &&
            this.microphoneStatus() !== DeviceStatus.denied
        ) {
            const preferredMic = this.microphones().find(
                (x) =>
                    x.deviceId === this.getPreferredDeviceStatus().microphoneID,
            );
            this.selectedMicrophone(
                preferredMic ?? this.microphones()[this.defaultMicrophoneIndex],
            );
        }

        if (
            this.speakers().length > 0 &&
            this.canSelectSpeaker() &&
            this.selectedSpeaker() === null &&
            this.speakerStatus() !== DeviceStatus.denied
        ) {
            const preferredSpeaker = this.speakers().find(
                (x) => x.deviceId === this.getPreferredDeviceStatus().speakerID,
            );
            this.selectedSpeaker(
                preferredSpeaker ?? this.speakers()[this.defaultSpeakerIndex],
            );
        }
    }

    private logDevice(
        kind: 'Camera' | 'Microphone' | 'Speaker',
        deviceID: string,
    ) {
        const device =
            this.cameras().find((d) => d.deviceId == deviceID) ??
            this.microphones().find((d) => d.deviceId == deviceID) ??
            this.speakers().find((d) => d.deviceId == deviceID);
        Log.info('Selected device', { kind, device });
    }

    private participantsChanged(ids: string[]) {
        if (ids.length === 0) {
            if (this.firstParticipantJoined()) {
                this.lastParticipantLeft(true);
                Log.info('Last participant left');
            }
        } else {
            this.firstParticipantJoined(true);
            this.lastParticipantLeft(false);
        }

        // Note that the count does not include us, but how many others are visible on-screen
        if (ids.length < 5) {
            this.selectedLayoutOption(SpeakerLayout._4_0);
        } else if (ids.length < 9) {
            this.selectedLayoutOption(SpeakerLayout._1_7);
        } else {
            this.selectedLayoutOption(SpeakerLayout._1_21);
        }
    }

    public shouldShowCycleCamera = ko.pureComputed(
        () => this.cameras().length > 1,
    );
    public isCycleCamButtonEnabled = ko.observable(true);

    private async getAvailableCameras() {
        if (navigator.mediaDevices?.enumerateDevices == null) {
            return [];
        }
        const devices = await navigator.mediaDevices.enumerateDevices();
        return devices.filter((d) => d.kind === 'videoinput');
    }

    private pexRTC: PexRTC;
    private videoSessionID: number | null;
    private uuid: string | undefined | null;
    private role = ko.observable<'HOST' | 'GUEST' | 'chair' | 'guest' | ''>('');

    private startTime: Date;
    private readonly joinedOn = new Date();
    private meetingStartedMinutesAgo = ko.observable<number>();

    public shouldAnimateLocalView = ko.observable(false);
    public cameraStatus = ko.observable(DeviceStatus.notRequested);
    public cameraPrivacy = ko.pureComputed(
        () => this.cameraStatus() !== DeviceStatus.granted,
    );
    public microphoneStatus = ko.observable(DeviceStatus.notRequested);
    public microphonePrivacy = ko.pureComputed(
        () => this.microphoneStatus() !== DeviceStatus.granted,
    );
    public microphoneDisabledOnConnect = ko.observable(false);
    public speakerStatus = ko.observable(DeviceStatus.notRequested);
    public isScreenShared = ko.observable(false);
    public isReceivingScreenshare = ko.observable(false);
    public messageFromCaregiver = ko.observable('');
    public errorDescription = ko.observable('');
    private participants = ko.observable<{
        [uuid: string]: ParticipantWithMetadata;
    }>({});
    public participantList = ko.observable([]);
    public firstParticipantJoined = ko.observable(false);
    public lastParticipantLeft = ko.observable(false);
    public participantStatus = ko.observable('');
    public disconnecting = ko.observable(false);

    public displayName = ko.observable<string>();
    public isHandRaised = ko.observable<boolean>(false);
    public numberOfHandsRaised = ko.observable<number>(0);

    public showCameraSelectionDropdown = ko.observable<boolean>(false);
    public showMicrophoneSelectionDropdown = ko.observable<boolean>(false);
    public showSpeakerSelectionDropdown = ko.observable<boolean>(false);

    public systemMessages = ko.pureComputed((): string[] => {
        const messages = [];
        switch (this.cameraStatus()) {
            case DeviceStatus.denied:
                messages.push(
                    'Kamera kunne ikke tilgås. Hvis du har givet adgang, tjek system-indstillinger.',
                );
                break;
            case DeviceStatus.notFound:
                messages.push(
                    'Kamera kunne ikke findes. Hvis du har tilsluttet et kamera, tryk på "Tænd kamera"-knappen nedenfor.',
                );
                break;
        }
        switch (this.microphoneStatus()) {
            case DeviceStatus.denied:
                messages.push(
                    'Mikrofon kunne ikke tilgås. Hvis du har givet adgang, tjek system-indstillinger.',
                );
                break;
            case DeviceStatus.notFound:
                messages.push(
                    'Mikrofon kunne ikke findes. Hvis du har tilsluttet en Mikrofon, tryk på "Tænd mikrofon"-knappen nedenfor.',
                );
                break;
        }

        return messages;
    });

    public warningMessage = ko.pureComputed((): string => {
        if (this.messageFromCaregiver().length > 0) {
            return this.messageFromCaregiver();
        } else if (!this.firstParticipantJoined()) {
            const meetingStartedMinutesAgo = this.meetingStartedMinutesAgo();
            if (meetingStartedMinutesAgo >= 60) {
                return `Det er ${meetingStartedMinutesAgo} min. siden din aftale var planlagt. Kontakt venligst afdelingen for at lave en ny aftale.`;
            } else if (meetingStartedMinutesAgo >= 15) {
                return `Det er ${meetingStartedMinutesAgo} min. siden din aftale var planlagt, vent venligst.`;
            }
        }
        return '';
    });

    public isLoadingVisible = ko.observable(true);
    public isUnsupportedDeviceVisible(): boolean {
        return false;
    }
    public isReady = ko.observable(false);
    public isFailedVisible(): boolean {
        return false;
    }
    public isFailedVersionVisible(): boolean {
        return false;
    }

    public disconnect(): void {
        if (this.disconnecting()) return;
        this.disconnecting(true);

        setTimeout(() => {
            this.pexRTC.disconnect();
            document.forms.namedItem('disconnect')?.submit();

            // this is defined in the cshtml files
            // eslint-disable-next-line
            const w = window as any;
            w.callWasDisconnected?.();
        }, 200);
    }
    public toggleScreenShare(): void {
        if (this.isScreenShared()) {
            Log.info('Stopping screenshare');
            this.pexRTC.present(null);
        } else {
            Log.info('Starting screenshare');
            this.pexRTC.present('screen');
        }
    }
    public cycleCamera(): void {
        if (!this.isCycleCamButtonEnabled()) {
            return;
        }

        this.isCycleCamButtonEnabled(false);
        setTimeout(() => {
            this.isCycleCamButtonEnabled(true);
        }, 2000);

        const currentCam = this.selectedCamera();
        const index = this.cameras().indexOf(currentCam);

        // If index is not found (-1), we take first.
        // If index is out-of-bounds, we take first.
        // If there are no first, we select null, which means no camera
        const nextCam =
            this.cameras()[index + 1] ??
            this.cameras()[this.defaultCameraIndex] ??
            null;
        if (currentCam === nextCam) {
            Log.info(
                `User tried to cycle cams, but got the same cam back. There are ${
                    this.cameras().length
                } cams in total.`,
            );
        } else {
            Log.info('User cycled cam', { currentCam, nextCam });
        }
        this.selectedCamera(nextCam);
    }
    public toggleMicrophone(): void {
        this.microphoneDisabledOnConnect(false);

        const status = this.microphoneStatus();
        const isMicBlockedByOS =
            status !== DeviceStatus.granted &&
            status !== DeviceStatus.disabledByUser;
        if (isMicBlockedByOS) {
            Log.info(
                'User hit toggle-mic button, but mic was blocked. Asking the OS for access',
            );
            this.requestDeviceAccess().catch((e) => {
                Log.error(e);
            });
            return;
        }
        this.microphoneStatus(
            status === DeviceStatus.granted
                ? DeviceStatus.disabledByUser
                : DeviceStatus.granted,
        );
        if (this.microphonePrivacy()) {
            Log.info('User disabled mic');
        } else {
            Log.info('User enabled mic');
        }
        this.setMutedForTracks(this.microphonePrivacy(), 'audio');

        // Update participant list with new microphone status
        this.updateParticipantListWithCurrentMuteStatus();

        // Inform other call participants that mute status has changed
        this.broadCastMicrophoneStatus();
    }

    private muteMicrophone(): void {
        this.microphoneDisabledOnConnect(false);

        const status = this.microphoneStatus();
        const isMicBlockedByOS =
            status !== DeviceStatus.granted &&
            status !== DeviceStatus.disabledByUser;
        if (isMicBlockedByOS) {
            Log.info(
                'Remote user hit mute-all button, but mic was blocked. Asking the OS for access',
            );
            this.requestDeviceAccess().catch((e) => {
                Log.error(e);
            });
            return;
        }

        this.microphoneStatus(DeviceStatus.disabledByUser);

        if (this.microphonePrivacy()) {
            Log.info('User disabled mic');
        } else {
            Log.info('User enabled mic');
        }
        this.setMutedForTracks(this.microphonePrivacy(), 'audio');

        // Update participant list with new microphone status
        this.updateParticipantListWithCurrentMuteStatus();

        // Inform other call participants that mute status has changed
        this.broadCastMicrophoneStatus();
    }
    public toggleCamera(): void {
        const status = this.cameraStatus();
        if (
            status !== DeviceStatus.granted &&
            status !== DeviceStatus.disabledByUser
        ) {
            Log.info(
                'User hit toggle-cam button, but cam was blocked. Asking the OS for access',
            );
            this.requestDeviceAccess().catch((e) => {
                Log.error(e);
            });
            return;
        }
        this.cameraStatus(
            status === DeviceStatus.granted
                ? DeviceStatus.disabledByUser
                : DeviceStatus.granted,
        );
        if (this.microphonePrivacy()) {
            Log.info('User disabled cam');
        } else {
            Log.info('User enabled cam');
        }
        this.setMutedForTracks(this.cameraPrivacy(), 'video');
    }

    private initPreflightForm(displayName: string): void {
        this.displayName.extend({
            required: { message: 'Navn skal udfyldes', param: true },
        });

        if (displayName != null && displayName != '') {
            this.displayName(displayName);
        } else {
            const fromCookie = Cookies.get('display_name');
            this.displayName(fromCookie);
        }

        this.displayName.subscribe((name): void => {
            Cookies.set('display_name', name, {
                sameSite: 'strict',
                secure: true,
            });
        });

        this.validator = ko.validatedObservable(this);
        this.validator.errors.showAllMessages(false);
    }

    public validatePreflight(): boolean {
        if (this.validator.isValid()) {
            return true;
        }
        this.validator.errors.showAllMessages();
        return false;
    }

    public toggleCameraSelectionDropdown(): void {
        this.showCameraSelectionDropdown(!this.showCameraSelectionDropdown());
    }

    public toggleMicrophoneSelectionDropdown(): void {
        this.showMicrophoneSelectionDropdown(
            !this.showMicrophoneSelectionDropdown(),
        );
    }

    public toggleSpeakerSelectionDropdown(): void {
        this.showSpeakerSelectionDropdown(!this.showSpeakerSelectionDropdown());
    }

    private setMutedForTracks(
        mutedValue: boolean,
        trackType: 'video' | 'audio',
    ) {
        if (this.connectToRemote) {
            if (trackType === 'video') {
                this.pexRTC.muteVideo(mutedValue);
            } else {
                this.pexRTC.muteAudio(this.microphonePrivacy());
            }
        } else {
            const src = this.localVideoElement?.srcObject;
            if (src instanceof MediaStream) {
                src.getTracks().forEach((x) => {
                    if (x.kind === trackType) {
                        x.enabled = !mutedValue;
                    }
                });
            }
        }
    }

    private logCurrentDeviceStatus(message: string) {
        const mic = this.translateDeviceStatusForLog(
            this.microphoneStatus(),
            this.microphones(),
            this.selectedMicrophone()?.deviceId,
        );
        const cam = this.translateDeviceStatusForLog(
            this.cameraStatus(),
            this.cameras(),
            this.selectedCamera()?.deviceId,
        );
        const speaker = this.translateDeviceStatusForLog(
            this.speakerStatus(),
            this.speakers(),
            this.selectedSpeaker()?.deviceId,
        );

        Log.info(`${message}. Cam: ${cam}. Mic: ${mic}. Speaker: ${speaker}`);
    }

    private translateDeviceStatusForLog(
        status: DeviceStatus,
        devices: MediaDeviceInfo[],
        current: string,
    ): string {
        let isMuted = false;
        switch (status) {
            case DeviceStatus.notRequested:
                return 'Not requested';
            case DeviceStatus.waitingForUser:
                return 'Waiting for user decision';
            case DeviceStatus.notFound:
                return 'Not found';
            case DeviceStatus.denied:
                return 'Access denied';
            case DeviceStatus.disabledByUser:
                isMuted = true;
            // falling through on purpose
            case DeviceStatus.granted:
                const id = current;
                const device =
                    devices.find((x) => x.deviceId === id) ?? devices[0];
                if (device == null) {
                    return 'Not found';
                }
                return `${device.label} (${device.deviceId}) [${
                    isMuted ? 'disabled' : 'enabled'
                } by user]`;
        }
    }

    setPreferredCameraDisabled(isDisabled: boolean) {
        sessionStorage.setItem('isCameraDisabled', isDisabled.toString());
    }
    setPreferredMicrophoneDisabled(isDisabled: boolean) {
        sessionStorage.setItem('isMicrophoneDisabled', isDisabled.toString());
    }
    setPreferredCamera(cameraID: string | null) {
        if (cameraID == null) {
            localStorage.removeItem('preferredCameraID');
        } else {
            localStorage.setItem('preferredCameraID', cameraID);
        }
    }
    setPreferredMicrophone(audioID: string | null) {
        if (audioID == null) {
            localStorage.removeItem('preferredMicrophoneID');
        } else {
            localStorage.setItem('preferredMicrophoneID', audioID);
        }
    }
    setPreferredSpeaker(audioID: string | null) {
        if (audioID == null) {
            localStorage.removeItem('preferredSpeakerID');
        } else {
            localStorage.setItem('preferredSpeakerID', audioID);
        }
    }
    getPreferredDeviceStatus(): {
        isCameraDisabled: boolean;
        isMicrophoneDisabled: boolean;
        cameraID: string | null;
        microphoneID: string | null;
        speakerID: string | null;
    } {
        return {
            isCameraDisabled:
                sessionStorage.getItem('isCameraDisabled') === 'true',
            isMicrophoneDisabled:
                sessionStorage.getItem('isMicrophoneDisabled') === 'true',
            cameraID: localStorage.getItem('preferredCameraID') ?? null,
            microphoneID: localStorage.getItem('preferredMicrophoneID') ?? null,
            speakerID: localStorage.getItem('preferredSpeakerID') ?? null,
        };
    }
}

export function VDXCall(
    node: string | null,
    conference: string | null,
    displayName: string | null,
    pin: number | null,
    meetingID: number | null,
    videoSessionID: number | null,
    startTime: string | null,
    remoteVideoElementId: string | null = 'remote',
    localVideoElementId: string | null = 'local',
    screenshareVideoElementId: string | null = 'screenshare',
    rootNode: HTMLElement = document.body,
): void {
    ko.applyBindings(
        new VDXCallViewModel(
            decodeHTML(node),
            decodeHTML(conference),
            decodeHTML(displayName),
            pin,
            meetingID,
            videoSessionID,
            startTime,
            remoteVideoElementId,
            localVideoElementId,
            screenshareVideoElementId,
        ),
        rootNode,
    );
}
